import type { Content } from "@tiptap/core";
import { EditorContent, useEditor, type Editor as EditorInstance } from "@tiptap/react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

import { createEditorExtensions } from "./extensions";
import { normalizeDocument } from "./normalize";
import { attachmentFilesFrom, imageFilesFrom, toAltText } from "./paste";
import type { ProseMirrorDocument } from "./serialize";
import { EditorToolbar, type ToolbarLabels } from "./toolbar";

/**
 * The WYSIWYG editor (TASK-401/405, SPEC §14).
 *
 * Two things are deliberately absent:
 *
 *   - **No upload client.** `uploadImage` and `uploadAttachment` are injected.
 *     The editor asks for a URL and inserts it; it never learns where the bytes
 *     went (AGENTS.md §13).
 *   - **No persistence.** `onChange` fires with the document and the caller
 *     decides what to do — the editor does not know about drafts, autosave or
 *     the API.
 */

/** What an uploaded attachment becomes in the document: a link plus its label. */
export interface InsertedAttachment {
  readonly url: string;
  readonly filename: string;
}

/** The commands the host page may run against the editor, via `ref`. */
export interface EditorHandle {
  /** Move the caret into the document. */
  focus(): void;
  /** Insert an image block at the caret. */
  insertImage(input: { readonly src: string; readonly alt?: string }): void;
  /** Insert a text link at the caret — how an attachment lands. */
  insertLink(input: { readonly href: string; readonly text: string }): void;
  /** Character count, as tracked by the `CharacterCount` extension. */
  characters(): number;
  /**
   * Replace the whole document — the import path.
   *
   * The caller owns persistence: like every other outside-in change, it does
   * not fire `onChange`, so the host updates its own document reference and
   * schedules its save.
   */
  replaceContent(document: ProseMirrorDocument): void;
}

export interface EditorProps {
  /**
   * Starting content.
   *
   * Either a ProseMirror document or an HTML string. HTML is what a draft
   * becomes: the stored form is Markdown, and the way from there to a document
   * is through the site's own renderer — so the editor opens on what the site
   * would actually show.
   */
  readonly initialContent?: ProseMirrorDocument | string | null;
  readonly placeholder?: string;
  /** Fires on every change with the current ProseMirror document. */
  readonly onChange?: (document: ProseMirrorDocument) => void;
  /**
   * Upload one image and return its public URL, or `null` to insert nothing.
   *
   * Called for pasted images, dropped images and files chosen through the
   * toolbar alike — the three paths in SPEC §15 are one function here.
   */
  readonly uploadImage?: (file: File) => Promise<string | null>;
  /**
   * Upload one non-image attachment and return its URL and display name.
   *
   * Attachments are links, not inline images: a PDF pasted or dropped onto the
   * editor becomes a `<a>` the reader clicks. `null` inserts nothing.
   */
  readonly uploadAttachment?: (file: File) => Promise<InsertedAttachment | null>;
  /**
   * Show the formatting toolbar above the document (default `true`).
   *
   * The toolbar only offers what the Markdown serializer can carry, so what
   * the author clicks is what publishes (AGENTS.md §11).
   */
  readonly toolbar?: boolean;
  /** Toolbar labels, for hosts that localize. English defaults otherwise. */
  readonly toolbarLabels?: ToolbarLabels;
  readonly editable?: boolean;
  readonly characterLimit?: number;
  readonly className?: string;
}

export const Editor = forwardRef<EditorHandle, EditorProps>(function Editor(
  {
    initialContent,
    placeholder,
    onChange,
    uploadImage,
    uploadAttachment,
    toolbar,
    toolbarLabels,
    editable = true,
    characterLimit,
    className,
  },
  ref,
) {
  // Held in refs so the paste/drop handlers, which ProseMirror captures once,
  // always call the current callbacks rather than the ones from first render.
  const uploadImageRef = useRef(uploadImage);
  const uploadAttachmentRef = useRef(uploadAttachment);
  const changeRef = useRef(onChange);

  useEffect(() => {
    uploadImageRef.current = uploadImage;
    uploadAttachmentRef.current = uploadAttachment;
    changeRef.current = onChange;
  }, [uploadImage, uploadAttachment, onChange]);

  const editorRef = useRef<EditorInstance | null>(null);

  const insertImageAt = useCallback(
    (editor: EditorInstance, src: string, alt?: string) => {
      editor.chain().focus().setImage({ src, ...(alt === undefined ? {} : { alt }) }).run();
    },
    [],
  );

  const insertLinkAt = useCallback((editor: EditorInstance, href: string, text: string) => {
    // A text node with a link mark plus a trailing space — no HTML string, so a
    // filename can never smuggle markup into the document.
    editor
      .chain()
      .focus()
      .insertContent([
        { type: "text", text, marks: [{ type: "link", attrs: { href } }] },
        { type: "text", text: " " },
      ])
      .run();
  }, []);

  const uploadAndInsert = useCallback(
    async (editor: EditorInstance, files: readonly File[]) => {
      const images = uploadImageRef.current;
      const attachments = uploadAttachmentRef.current;

      for (const file of files) {
        if (isImage(file)) {
          if (images === undefined) {
            continue;
          }

          const url = await images(file);

          if (url !== null) {
            insertImageAt(editor, url, toAltText(file.name));
          }

          continue;
        }

        if (attachments === undefined) {
          continue;
        }

        const uploaded = await attachments(file);

        if (uploaded !== null) {
          insertLinkAt(editor, uploaded.url, uploaded.filename);
        }
      }
    },
    [insertImageAt, insertLinkAt],
  );

  const editor = useEditor({
    editable,
    extensions: createEditorExtensions({
      ...(placeholder === undefined ? {} : { placeholder }),
      ...(characterLimit === undefined ? {} : { characterLimit }),
    }),
    // Our node type keeps `content` readonly, because it describes data coming
    // *out* of ProseMirror. TipTap's `Content` wants a mutable array, so the
    // assertion is confined to this one hand-off. A string passes through
    // unchanged — TipTap parses HTML itself.
    ...(initialContent === undefined || initialContent === null
      ? {}
      : { content: initialContent as unknown as Content }),
    editorProps: {
      attributes: {
        class: "prose prose-neutral max-w-none focus:outline-none",
        ...(placeholder === undefined ? {} : { "data-placeholder": placeholder }),
      },

      /**
       * Ctrl/Cmd+V with files on the clipboard (SPEC §15, TASK-405).
       *
       * Returning `true` tells ProseMirror the event is handled, so it does not
       * also try to paste the image as HTML.
       */
      handlePaste: (_view, event) => {
        const files = [
          ...imageFilesFrom(event.clipboardData),
          ...attachmentFilesFrom(event.clipboardData),
        ];

        if (files.length === 0 || editor === null) {
          return false;
        }

        event.preventDefault();
        void uploadAndInsert(editor, files);

        return true;
      },

      /** Dragging a file onto the editor. */
      handleDrop: (_view, event, _slice, moved) => {
        // An internal drag (moving a node) must keep its default behaviour.
        if (moved) {
          return false;
        }

        const files = [
          ...imageFilesFrom(event.dataTransfer),
          ...attachmentFilesFrom(event.dataTransfer),
        ];

        if (files.length === 0 || editor === null) {
          return false;
        }

        event.preventDefault();
        void uploadAndInsert(editor, files);

        return true;
      },
    },
    onUpdate: ({ editor: instance }) => {
      const doc = instance.getJSON() as ProseMirrorDocument;
      const normalized = normalizeDocument(doc);

      // Paste is the one edit path normalization does not see at a boundary,
      // and it is exactly where residue arrives (markdown copied out of
      // another tool carries `\*`-style stacks). The corrected document goes
      // back silently — `setContent` does not re-enter `onUpdate` — so what
      // the host saves is what a reader would see.
      if (normalized !== doc) {
        instance.commands.setContent(normalized as unknown as Content, false);
      }

      changeRef.current?.(normalized);
    },
  });

  editorRef.current = editor ?? null;

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editorRef.current?.commands.focus();
      },
      insertImage: ({ src, alt }: { readonly src: string; readonly alt?: string }) => {
        if (editorRef.current !== null) {
          insertImageAt(editorRef.current, src, alt);
        }
      },
      insertLink: ({ href, text }: { readonly href: string; readonly text: string }) => {
        if (editorRef.current !== null) {
          insertLinkAt(editorRef.current, href, text);
        }
      },
      characters: () => {
        const storage = editorRef.current?.storage.characterCount as
          | { characters?: () => number }
          | undefined;

        return storage?.characters?.() ?? 0;
      },
      replaceContent: (document: ProseMirrorDocument) => {
        // The same hand-off assertion as `initialContent`: our read-only node
        // type against TipTap's mutable `Content`.
        editorRef.current?.commands.setContent(document as unknown as Content);
      },
    }),
    [insertImageAt, insertLinkAt],
  );

  // `useEditor` starts nullish on the first render; the toolbar only mounts
  // once there is an instance to drive.
  return (
    <>
      {editor != null && editable && toolbar !== false ? (
        <EditorToolbar editor={editor} labels={toolbarLabels} />
      ) : null}
      <EditorContent editor={editor} className={className} />
    </>
  );
});

/**
 * A paste can hand over a File whose `type` is empty (a renamed file on some
 * platforms); those are treated as attachments, and the server's allow-list has
 * the final word anyway.
 */
function isImage(file: File): boolean {
  return file.type.toLowerCase().startsWith("image/");
}
